import React, { useContext, useRef, useState, useEffect } from 'react';
import algoliasearch from 'algoliasearch/lite';
import { MdClose } from 'react-icons/md';
import {
  ClearRefinements,
  CurrentRefinements,
  Configure,
  InstantSearch,
  RefinementList,
  SortBy,
  useCurrentRefinements,
  useHits,
  type UseHitsProps,
  usePagination,
  useRefinementList,
} from 'react-instantsearch';
import { graphql, navigate, Link, PageProps } from 'gatsby';
import styled from 'styled-components';

import { SanityCollection } from '@repo/models/gatsby-graphql-types';
import type {
  PortableTextBlock,
  PortableTextListItemBlock,
} from '@portabletext/types';

import { CartContext } from '@repo/contexts/CartContext';
import { LocaleContext } from '@repo/contexts/LocaleContext';

import AdditionalLinks from '@repo/ui-components/collections/AdditionalLinks';
import breakpoints from '@repo/ui-components/breakpoints';
import BlockBuilderLinkGrid from '@repo/ui-components/sanity/types/BlockBuilderLinkGrid/BlockBuilderLinkGrid';
import BlockContent from '@repo/ui-components/sanity/BlockContent';
import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import Hero from '@repo/ui-components/hero/Hero';
import ProductCard from '@repo/ui-components/product-card/ProductCard';
import ProductCardGrid from '@repo/ui-components/ProductCardGrid';
import { NumericMenu } from '@repo/ui-components/algolia/NumericMenu';
import sharedStyles from '@repo/ui-components/styles';

import createOpenGraphTags from '@repo/utils/createOpenGraphTags';

import getInternalLink from '@components/utils/getInternalLink';
import Layout from '@components/layout';
import CustomPagination from '@components/collections/CustomPagination';

import 'instantsearch.css/themes/satellite.css';

interface CollectionListProps {
  title?: string;
  slug?: string;
}

const searchClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY
);

const ContentContainer = styled.div<{ widthPercent?: string }>`
  flex: 1;
  padding: ${sharedStyles.spacing['spacing-lg']};
  ${sharedStyles.spacing['spacing-lg']};
  padding-bottom: 0;
  width: ${({ widthPercent }) => widthPercent}%;

  @media (max-width: ${breakpoints.sm}) {
    padding: 0 1rem;
  }
`;

const MarketingTop = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f7f7f7;

  // hide on smaller viewports
  @media (max-width: ${breakpoints.lg}) {
    font-size: 0;
    margin: 0;
    padding: 0;
    height: 0;
    overflow: hidden;
    border: none;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #f7f7f7;
  margin-bottom: 1rem;

  // row on smaller viewports
  @media (max-width: ${breakpoints.sm}) {
    flex-direction: column;
    // reverse
    flex-direction: column-reverse;
    border: none;
    margin: 0;
    padding: 0;
  }
`;

const SearchOptionsPanel = styled.div`
  border-right: 1px solid #f7f7f7;
  padding-right: 1rem;
  margin-right: 1rem;
  width: 240px;
  @media (max-width: ${breakpoints.sm}) {
    display: none;
  }
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 2rem;
  z-index: 1;

  h2 {
    font-size: 1.5rem;
  }
`;

const SearchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f7f7f7;
  padding: 1rem 0;
  background-color: #fff;
`;

const SearchResultsPanel = styled.div`
  flex: 1;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  max-width: 100%;
`;

const CurrentRefinementsContainer = styled.div`
  @media (max-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const SortContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  @media (max-width: ${breakpoints.lg}) {
    flex: 1;
  }

  select,
  button {
    flex: 1;
  }
`;

const NoResultsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 2rem;
  height: 130px;
`;

const RefinementContainer = styled.div`
  padding-top: 1rem;

  h2 {
    font-size: 1.5rem;
  }

  .ais-RefinementList-checkbox:focus {
    color: #0067b1;
    box-shadow: rgba(35, 38, 59, 0.05) 0 1px 0 0 inset, #0067b1 0 0 0 1px;
  }
`;

const FiltersModal = styled.div<{ visible?: boolean }>`
  background: #fff;
  position: fixed;
  z-index: 99999;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  opacity: 1;
  transition: opacity 0.15s ease-in-out;
  overflow-y: auto;
  padding: 1rem;
  transform: ${({ visible }) =>
    visible ? 'translateY(0)' : 'translateY(-100%)'};
  transition: transform 0.25s ease;
  // hide on larger viewports
  @media (min-width: ${breakpoints.sm}) {
    display: none !important;
  }

  .close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    cursor: pointer;
    fill: ${({ theme }) => theme.colors.defaultFont};
  }
`;

const RefinementCountBubble = styled.div`
  display: flex;
  color: white;
  background: ${({ theme }) => theme.colors.brandPrimary};
  font-size: 0.625rem;
  border-radius: 50%;
  width: 17px;
  height: 17px;
  align-items: center;
  justify-content: center;
  font-weight: normal;
  margin-left: 0.25rem;
`;

const FilterButton = styled.button`
  flex: 1;
  position: relative;
  @media (min-width: ${breakpoints.sm}) {
    display: none !important;
  }
`;

interface HitVariantProps {
  store?: {
    price: number;
    sku?: string;
  };
  canadaStore?: {
    price?: number;
    compareAtPrice?: number;
  };
}

interface HitProps {
  hit?: {
    objectID?: string;
    title?: string;
    description?: string;
    slug?: {
      current?: string;
    };
    type?: string;
    price?: number;
    previewImageUrl?: string;
    priceRange?: {
      minVariantPrice?: number;
      maxVariantPrice?: number;
    };
    cadPriceRange?: {
      minVariantPrice?: number;
      maxVariantPrice?: number;
    };
    variants?: HitVariantProps[];
    features?: string[];
    cadCompareAtPrice?: number;
    cadPrice?: number;
  };
  sanityCollection?: SanityCollection;
}

function Hit({ hit, sanityCollection }: Readonly<HitProps>) {
  const product = {
    id: hit.objectID,
    title: hit.title,
    description: hit.description,
    handle: hit.slug.current,
    type: hit.type,
    price: hit.price,
    image: {
      src: hit.previewImageUrl,
      altText: '',
    },
    priceRangeV2: {
      minVariantPrice: {
        amount: hit.priceRange.minVariantPrice,
      },
      maxVariantPrice: {
        amount: hit.priceRange.maxVariantPrice,
      },
    },
    cadPriceRangeV2: {
      minVariantPrice: {
        amount: hit.cadPriceRange.minVariantPrice,
      },
      maxVariantPrice: {
        amount: hit.cadPriceRange.maxVariantPrice,
      },
    },
    variants: hit.variants.map(variant => ({
      // return all of variant.store
      ...variant.store,
    })),
    features: hit.features,
    sku: hit.variants[0]?.store?.sku,
  };

  return (
    <ProductCard
      key={hit.objectID}
      product={product}
      showFeatureList={sanityCollection?.showFeatureList}
      showRatings
      sanityProduct={{
        store: {
          variants: hit.variants.map(variant => ({
            canadaStore: {
              compareAtPrice: variant?.canadaStore?.compareAtPrice,
              price: variant?.canadaStore?.price,
            },
          })),
        },
      }}
    />
  );
}

type HitsContainerProps = UseHitsProps & {
  sanityCollection?: SanityCollection;
};

function HitsContainer(props: Readonly<HitsContainerProps>) {
  const { sanityCollection } = props;
  const { hits } = useHits(props);

  if (hits.length === 0) {
    return <NoResultsContainer>No results found</NoResultsContainer>;
  }

  return (
    <ProductCardGrid padding={0} showRatings>
      {hits.map(hit => (
        <Hit key={hit.objectID} hit={hit} sanityCollection={sanityCollection} />
      ))}
    </ProductCardGrid>
  );
}

const transformItemLabels = items =>
  items.map(item => {
    let { label } = item;

    if (label === 'options.values') {
      label = 'Options';
    }

    if (label === 'cadPrice' || label === 'price') {
      label = 'Price';
    }

    if (label === 'color') {
      label = 'Color';
    }

    if (label === 'size') {
      label = 'Size';
    }

    return {
      ...item,
      label,
    };
  });

interface PriceRefinementListProps {
  currency: 'CAD' | 'USD';
}

function PriceRefinementList({ currency }: Readonly<PriceRefinementListProps>) {
  const currrencySymbol = currency === 'CAD' ? 'CA$' : '$';

  return (
    <RefinementContainer>
      <h2>Price Range</h2>
      <NumericMenu
        attribute={currency === 'CAD' ? 'cadPrice' : 'price'}
        items={[
          { label: `0 to ${currrencySymbol}10`, end: 10 },
          {
            label: `${currrencySymbol}10 to ${currrencySymbol}50`,
            start: 10,
            end: 50,
          },
          {
            label: `${currrencySymbol}50 to ${currrencySymbol}75`,
            start: 50,
            end: 75,
          },
          {
            label: `${currrencySymbol}75 to ${currrencySymbol}100`,
            start: 75,
            end: 100,
          },
          { label: `${currrencySymbol}100+`, start: 100 },
        ]}
        transformItems={transformItemLabels}
      />
    </RefinementContainer>
  );
}

const getShowMoreButtonText = (isShowingMore, title) => {
  let text = isShowingMore ? 'Show Less ' : 'Show More ';
  if (title === 'Options') {
    text += 'Options';
  } else {
    text += `${title}s`;
  }
  return text;
};

interface ProductRefinementListProps {
  attribute: string;
  title: string;
}

function ProductRefinementList({
  attribute,
  title,
}: Readonly<ProductRefinementListProps>) {
  const { items } = useRefinementList({ attribute });

  return (
    items &&
    items.length > 0 && (
      <RefinementContainer>
        <h2>{title}</h2>
        <RefinementList
          showMore
          translations={{
            showMoreButtonText: ({ isShowingMore }) =>
              getShowMoreButtonText(isShowingMore, title),
          }}
          limit={10}
          attribute={attribute}
          sortBy={['count:desc', 'name:asc']}
          transformItems={transformItemLabels}
        />
      </RefinementContainer>
    )
  );
}

function generateCollectionListWithBrandPrioritized(collectionList) {
  // Separate the slugs into constants for better readability
  const excludedSlugs = ['canada-store-products', 'subscriptions'];
  const brandSlugs = [
    'batteries',
    'power-cap-units-and-batteries',
    'collar-straps',
    'shields-avoidance',
    'system-parts',
  ];

  // Separate collections based on slugs
  const brandCollectionList = collectionList
    .filter(collection => brandSlugs.includes(collection.slug))
    .sort((a, b) => a.title.localeCompare(b.title));

  const otherCollectionList = collectionList
    .filter(
      collection =>
        !brandSlugs.includes(collection.slug) &&
        !excludedSlugs.includes(collection.slug)
    )
    .sort((a, b) => a.title.localeCompare(b.title));

  // Combine the collections into one list, inserting the brand collections after the first collection in other collections
  const collectionsWithBrandPrioritized = [
    ...otherCollectionList.slice(0, 1),
    ...brandCollectionList,
    ...otherCollectionList.slice(1),
  ];

  return collectionsWithBrandPrioritized;
}

interface AllCollectionsDropDownProps {
  collectionList: CollectionListProps[];
  currentCollectionSlug: string;
}

function AllCollectionsDropDown({
  collectionList,
  currentCollectionSlug,
}: Readonly<AllCollectionsDropDownProps>) {
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';
  const [selectedCollection, setSelectedCollection] = useState(
    currentCollectionSlug
  );

  const collectionsWithBrandPrioritized =
    generateCollectionListWithBrandPrioritized(collectionList);

  const handleChange = event => {
    setSelectedCollection(event.target.value);
    // gatsby navigate to collection page

    const pageUrl = currency === 'CAD' ? `/en-ca/shop` : `/shop`;

    if (event.target.value === 'shop') {
      navigate(pageUrl);
    } else {
      navigate(`${pageUrl}/${event.target.value}`);
    }
  };

  return (
    <div
      className="ais-SortBy"
      style={{
        flex: 1,
        fontSize: '.85rem',
      }}
    >
      <select
        className="ais-SortBy-select"
        value={selectedCollection}
        onChange={handleChange}
        aria-label="Go to"
      >
        {collectionsWithBrandPrioritized.map(collection => (
          <option key={collection.slug} value={collection.slug}>
            {collection.title}
          </option>
        ))}
      </select>
    </div>
  );
}

interface AllCollectionsListProps {
  collectionList?: CollectionListProps[];
}

function AllCollectionsList({
  collectionList,
}: Readonly<AllCollectionsListProps>) {
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';
  const collectionsWithBrandPrioritized =
    generateCollectionListWithBrandPrioritized(collectionList).filter(
      collection =>
        // remove a bunch of collections from this list
        collection.slug !== 'shop' &&
        collection.slug !== 'canada-store-products' &&
        collection.slug !== 'subscriptions'
    );

  return (
    <div
      style={{
        marginBottom: '1rem',
        paddingTop: '1rem',
        // hide on larger viewports
      }}
    >
      <h2>Go To</h2>
      <ul className="ais-Menu-list">
        {collectionsWithBrandPrioritized.map(collection => (
          <li className="ais-SortBy-item" key={collection.slug}>
            <Link
              className="ais-SortBy-link"
              to={
                currency === 'CAD'
                  ? `/en-ca/shop/${collection.slug}`
                  : `/shop/${collection.slug}`
              }
              style={{
                textDecoration: 'none',
              }}
            >
              {collection.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CurrentRefinementCount() {
  const { items } = useCurrentRefinements();

  if (items.length > 0) {
    let refinementCount = 0;

    items.forEach(item => {
      refinementCount += item.refinements.length;
    });

    return <RefinementCountBubble>{refinementCount}</RefinementCountBubble>;
  }

  return null;
}

const getPriceRange = (priceRefinements, formatPrice) => {
  if (priceRefinements.length === 2) {
    return `${formatPrice(priceRefinements[0].value).replace(
      '.00',
      ''
    )} to ${formatPrice(priceRefinements[1].value).replace('.00', '')}`;
  }

  if (priceRefinements[0].value === 10) {
    return `${formatPrice(0).replace('.00', '')} to ${formatPrice(
      priceRefinements[0].value
    ).replace('.00', '')}`;
  }

  if (priceRefinements[0].value === 100) {
    return `${formatPrice(priceRefinements[0].value).replace('.00', '+')}`;
  }

  return formatPrice(priceRefinements[0].value).replace('.00', '');
};

const getRemoveButtonAriaLabel = (item, refinement) => {
  let { label } = item;

  if (label === 'Options') {
    label = 'Option';
  }

  return `Remove ${label} ${refinement.label}`;
};

function CustomCurrentRefinements(props) {
  const { formatPrice } = useContext(CartContext);
  const { items, refine } = useCurrentRefinements(props);

  if (items.length > 0) {
    return (
      <ul className="ais-CurrentRefinements-list">
        {items.map(item => {
          const priceRefinements = item.refinements.filter(
            refinement =>
              refinement.attribute === 'price' ||
              refinement.attribute === 'cadPrice'
          );

          const filteredItems = item.refinements.filter(
            refinement =>
              refinement.attribute !== 'price' &&
              refinement.attribute !== 'cadPrice'
          );

          return (
            <li
              key={[item.indexName, item.label].join('/')}
              className="ais-CurrentRefinements-item"
            >
              <span className="ais-CurrentRefinements-label">
                {item.label}:
              </span>

              {filteredItems.map(refinement => (
                <span
                  key={refinement.label}
                  className="ais-CurrentRefinements-category"
                >
                  <span className="ais-CurrentRefinements-categoryLabel">
                    {refinement.label}
                  </span>

                  <button
                    type="button"
                    className="ais-CurrentRefinements-delete"
                    onClick={() => {
                      refine(refinement);
                    }}
                    aria-label={getRemoveButtonAriaLabel(item, refinement)}
                  >
                    ✕
                  </button>
                </span>
              ))}

              {priceRefinements?.length > 0 && (
                <span className="ais-CurrentRefinements-category">
                  <span className="ais-CurrentRefinements-categoryLabel">
                    {getPriceRange(priceRefinements, formatPrice)}
                  </span>

                  <button
                    type="button"
                    className="ais-CurrentRefinements-delete"
                    onClick={() => {
                      priceRefinements.forEach(priceRefinement => {
                        refine(priceRefinement);
                      });
                    }}
                  >
                    ✕
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return <CurrentRefinements transformItems={transformItemLabels} />;
}

function formatPageTitle(
  title,
  pageNumber,
  siteSuffix = '| Invisible Fence Brand'
) {
  const pageNumberTitle = pageNumber > 0 ? `Page ${pageNumber} ` : ``;
  if (!title) return `${pageNumberTitle}${siteSuffix}`;

  const suffixIndex = title.indexOf(siteSuffix);

  if (suffixIndex !== -1) {
    // Title already contains the suffix – insert page number info before it
    const baseTitle = title.slice(0, suffixIndex).trim();

    if (pageNumber > 0) {
      return `${baseTitle} - ${pageNumberTitle}${siteSuffix}`;
    }

    return `${baseTitle} ${siteSuffix}`;
  }

  if (pageNumber > 0) {
    return `${title} - ${pageNumberTitle}${siteSuffix}`;
  }

  return `${title} ${siteSuffix}`;
}

interface UpdateCurrentPageProps {
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

function UpdateCurrentPage({ setPage }: Readonly<UpdateCurrentPageProps>) {
  const { currentRefinement } = usePagination();

  useEffect(() => {
    // Algolia pages are 0-indexed, but we want 1-based
    setPage(currentRefinement + 1);
  }, [currentRefinement, setPage]);

  return null;
}

type SanityBlock = PortableTextBlock | PortableTextListItemBlock;

type SanityCollectionCustomProps = SanityCollection & {
  bodyTop?: SanityBlock[];
  body?: SanityBlock[];
};

type CollectionPageProps = {
  pageContext?: {
    slug?: string;
    id?: string;
    collectionList?: CollectionListProps[];
  };
  data?: {
    sanityCollection?: SanityCollectionCustomProps;
  };
} & PageProps;

function CollectionPage({
  data = {},
  pageContext = {},
  location,
}: Readonly<CollectionPageProps>) {
  const { collectionList } = pageContext;
  const { sanityCollection } = data;
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';
  const { slug } = pageContext;
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);

  const breadcrumbs = [
    { text: 'Home', link: currency === 'CAD' ? '/en-ca' : '/' },
  ];

  const isPartsAndAccessories = sanityCollection?.store?.title?.includes(
    'Parts & Accessories'
  );
  const isNotAccessoriesSlug =
    sanityCollection?.store?.slug?.current !== 'accessories';

  if (isPartsAndAccessories && isNotAccessoriesSlug) {
    breadcrumbs.push({
      text: 'Parts & Accessories',
      link: currency === 'CAD' ? '/en-ca/accessories' : '/accessories',
    });
  } else if (sanityCollection?.parentCollection) {
    const {
      title,
      slug: { current },
    } = sanityCollection.parentCollection.store;
    breadcrumbs.push({
      text: title,
      link: currency === 'CAD' ? `/en-ca/${current}` : `/${current}`,
    });
  }

  breadcrumbs.push({
    text: sanityCollection?.store?.title?.replace('Parts & Accessories -', ''),
    link: '',
  });

  const marketingRef = useRef(null);

  const [windowWidth, setWindowWidth] = useState(
    // if browser, set to window width
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  });

  return (
    <Layout
      location={location}
      includeBVScript
      headTagData={{
        title: formatPageTitle(
          sanityCollection?.SEOMetadata?.title ||
            sanityCollection?.store?.title,
          currentPage
        ),
        description:
          sanityCollection?.SEOMetadata?.description ||
          sanityCollection?.description,
        canonicalUrl: sanityCollection?.SEOMetadata?.canonicalUrl,
        noIndex: sanityCollection?.SEOMetadata?.noIndex,
        noFollow: sanityCollection?.SEOMetadata?.noFollow,
        includeBVScript: true,
        meta: createOpenGraphTags(
          {
            title: formatPageTitle(
              sanityCollection?.SEOMetadata?.ogTitle ||
                sanityCollection?.SEOMetadata?.title ||
                sanityCollection?.store?.title,
              currentPage
            ),
            description:
              sanityCollection?.SEOMetadata?.ogDescription ||
              sanityCollection?.SEOMetadata?.description ||
              sanityCollection?.description,
            url:
              sanityCollection?.SEOMetadata?.ogUrl ||
              sanityCollection?.SEOMetadata?.canonicalUrl ||
              (typeof window !== 'undefined' && window?.location?.pathname) ||
              '/',
            image:
              sanityCollection?.SEOMetadata?.ogImage ||
              sanityCollection?.image ||
              '',
          },
          'invisiblefence'
        ),
      }}
    >
      <BreadcrumbWithWrapper elements={breadcrumbs} />

      <ContentContainer>
        {(sanityCollection?.hero?.mediaItem ||
          sanityCollection?.hero?.heroType) && (
          <Hero
            hero={sanityCollection?.hero}
            getInternalLink={getInternalLink}
          />
        )}

        <MarketingTop>
          {!sanityCollection?.hero?.mediaItem &&
            !sanityCollection?.hero?.heroType && (
              <h1>{sanityCollection.store.title}</h1>
            )}
          <BlockContent
            blocks={sanityCollection.bodyTop}
            getInternalLink={getInternalLink}
          />
          {(sanityCollection?.additionalLinks?.length > 0 ||
            sanityCollection?.body ||
            sanityCollection?.relatedArticles?.length > 0) && (
            <a
              href="#marketing-content" // don't show this when nothing in body, fix dropdowns
              onClick={() => marketingRef.current.scrollIntoView()}
            >
              Learn More
            </a>
          )}
        </MarketingTop>

        <InstantSearch
          searchClient={searchClient}
          indexName={`collection_${slug}`}
          routing
        >
          <UpdateCurrentPage setPage={setCurrentPage} />
          {
            // remove from DOM on larger than breakpoint sm
            windowWidth < parseInt(breakpoints.sm, 10) && (
              <FiltersModal visible={showFiltersModal}>
                <MdClose
                  size={32}
                  className="close-button"
                  onClick={() => setShowFiltersModal(false)}
                />
                <div style={{ height: '2rem', marginTop: '2rem' }}>
                  <CustomCurrentRefinements
                    transformItems={transformItemLabels}
                  />
                </div>
                <PriceRefinementList currency={currency} />
                <ProductRefinementList
                  attribute="options.values"
                  title="Options"
                />
                <ProductRefinementList attribute="color" title="Color" />
                <ProductRefinementList attribute="size" title="Size" />
                <RefinementContainer>
                  <ClearRefinements
                    translations={{
                      resetButtonText: 'Clear All',
                    }}
                  />
                </RefinementContainer>
              </FiltersModal>
            )
          }

          <Configure
            // filter products by currency locale
            filters={currency === 'CAD' ? 'tags:sold-in-canada' : ''}
            hitsPerPage={12}
          />

          <SearchContainer>
            {
              // remove from DOM on larger than breakpoint sm
              windowWidth >= parseInt(breakpoints.sm, 10) && (
                <SearchOptionsPanel>
                  <StickyContainer>
                    {
                      // if root collection show all collections list
                      sanityCollection?.store?.slug?.current === 'shop' && (
                        <AllCollectionsList collectionList={collectionList} />
                      )
                    }

                    <PriceRefinementList currency={currency} />
                    <ProductRefinementList
                      attribute="options.values"
                      title="Options"
                    />
                    <ProductRefinementList attribute="color" title="Color" />
                    <ProductRefinementList attribute="size" title="Size" />
                    <RefinementContainer>
                      <ClearRefinements
                        translations={{
                          resetButtonText: 'Clear All',
                        }}
                      />
                    </RefinementContainer>
                  </StickyContainer>
                </SearchOptionsPanel>
              )
            }
            <SearchResultsPanel>
              <StickyContainer>
                <SearchHeader>
                  <CurrentRefinementsContainer>
                    <CustomCurrentRefinements
                      transformItems={transformItemLabels}
                    />
                  </CurrentRefinementsContainer>
                  <SortContainer>
                    <AllCollectionsDropDown
                      collectionList={collectionList}
                      currentCollectionSlug={
                        sanityCollection?.store?.slug?.current
                      }
                    />
                    <SortBy
                      style={{
                        flex: 1,
                        fontSize: '.85rem',
                      }}
                      aria-label="Sort by"
                      items={[
                        { label: 'Featured', value: `collection_${slug}` },
                        {
                          label:
                            currency === 'CAD'
                              ? 'Price CAD (asc)'
                              : 'Price (asc)',
                          value:
                            currency === 'CAD'
                              ? `collection_${slug}_CAD_price_asc`
                              : `collection_${slug}_price_asc`,
                        },
                        {
                          label:
                            currency === 'CAD'
                              ? 'Price CAD (desc)'
                              : 'Price (desc)',
                          value:
                            currency === 'CAD'
                              ? `collection_${slug}_CAD_price_desc`
                              : `collection_${slug}_price_desc`,
                        },
                      ]}
                    />
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <FilterButton
                        // use algolia button class
                        className="ais-RelevantSort-button"
                        onClick={e => {
                          e.preventDefault();
                          setShowFiltersModal(!showFiltersModal);
                        }}
                        type="button"
                      >
                        Filters <CurrentRefinementCount />
                      </FilterButton>
                    </div>
                  </SortContainer>
                </SearchHeader>
              </StickyContainer>
              <HitsContainer sanityCollection={sanityCollection} />
              <PaginationContainer>
                <CustomPagination />
              </PaginationContainer>
            </SearchResultsPanel>
          </SearchContainer>
        </InstantSearch>

        <div id="marketing-content" ref={marketingRef} />

        {sanityCollection?.additionalLinks?.length > 0 && (
          <AdditionalLinks
            links={sanityCollection.additionalLinks}
            getInternalLink={getInternalLink}
          />
        )}

        {sanityCollection?.body && (
          <BlockContent
            blocks={sanityCollection.body}
            getInternalLink={getInternalLink}
          />
        )}

        {sanityCollection?.relatedArticles?.length > 0 && (
          <>
            <h2>RELATED ARTICLES</h2>

            <BlockBuilderLinkGrid
              value={{
                items: sanityCollection.relatedArticles,
              }}
              getInternalLink={getInternalLink}
            />
          </>
        )}
      </ContentContainer>
    </Layout>
  );
}

// collection graphql query
export const query = graphql`
  query GetSanityCollection($id: String!) {
    sanityCollection(id: { eq: $id }) {
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      hero {
        presentation
        heroType
        compactImage
        mediaItem {
          asset {
            _id
          }
          crop {
            bottom
            left
            right
            top
          }
          hotspot {
            x
            y
          }
          alt
        }
        externalVideo
        internalVideo {
          video: asset {
            url
          }
        }
        heroPoster {
          asset {
            url
          }
        }
        headline1
        headline2
        headlineStyle
        subheadType
        portableTextHeadline: _rawPortableTextHeadline(
          resolveReferences: { maxDepth: 2 }
        )
        subheadlineStyle
        textAlignment
        enableTextShadow
        enableCta
        cta {
          linkType
          internalLink: _rawInternalLink(resolveReferences: { maxDepth: 2 })
          caption
          href
        }
        themeStyle
      }
      store: _rawStore(resolveReferences: { maxDepth: 3 })
      description
      image {
        asset: _rawAsset(resolveReferences: { maxDepth: 3 })
        crop: _rawCrop(resolveReferences: { maxDepth: 3 })
        hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
        alt
      }
      parentCollection {
        store {
          title
          slug {
            current
          }
        }
      }
      bodyTop: _rawBodyTop(resolveReferences: { maxDepth: 5 })
      body: _rawBody(resolveReferences: { maxDepth: 5 })
      relatedArticles {
        id
        _key
        _type
        title
        slug {
          current
        }
        image: _rawImage(resolveReferences: { maxDepth: 3 })
      }
      additionalLinks {
        ... on SanityLink {
          _key
          _type
          caption
          blank
          href
          internalLink: _rawInternalLink(resolveReferences: { maxDepth: 3 })
        }
      }
      showFeatureList
    }
  }
`;

export default CollectionPage;
